import {
  AppShellContent,
  Button,
  Group,
  Stack,
  Text,
  Title,
} from "@assetwatch/ui";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  MotionAmplificationJob,
  MotionAmplificationJobId,
} from "../clients/motion-amplification";
import { MotionAmplificationDetailsModal } from "../components/motion-amplification/MotionAmplificationDetailsModal";
import { MotionAmplificationJobFilters } from "../components/motion-amplification/MotionAmplificationJobFilters";
import { MotionAmplificationRetryPrompt } from "../components/motion-amplification/MotionAmplificationRetryPrompt";
import { MotionAmplificationUploadModal } from "../components/motion-amplification/MotionAmplificationUploadModal";
import {
  defaultMotionAmplificationFilters,
  matchesMotionAmplificationFilters,
} from "../components/motion-amplification/motionAmplificationFilters";
import type { JobView } from "../components/motion-amplification/types";
import { MotionAmplificationGalleryAdapter } from "../components/snowflakes/MotionAmplificationGalleryAdapter";
import { useRetryMotionAmplificationJobMutation } from "../mutations/useRetryMotionAmplificationJobMutation";
import type { ActiveMotionAmplificationUpload } from "../mutations/useUploadMotionAmplificationVideoMutation";
import { useUploadMotionAmplificationVideoMutation } from "../mutations/useUploadMotionAmplificationVideoMutation";
import { useMotionAmplificationRawVideosQuery } from "../queries/useMotionAmplificationRawVideosQuery";

type MotionAmplificationJobsQueryState = {
  data: MotionAmplificationJob[] | undefined;
  error: Error | null;
  isLoading: boolean;
};

type FailedJobDecision = {
  jobId: MotionAmplificationJobId;
  jobName: string;
};

function defaultUploadState(
  job: MotionAmplificationJob,
): JobView["uploadState"] {
  if (job.status === "Completed") return "completed";
  if (job.status === "Failed") return "failed";
  return "processing";
}

/**
 * The server has no concept of "uploading"/"starting" - those only exist
 * while the client is PUTting the file to S3 and waiting for the job to
 * start. `activeUpload` overlays that client-only progress onto whichever
 * job it belongs to; every other job's view is derived purely from its
 * server status.
 */
function toJobView(
  job: MotionAmplificationJob,
  activeUpload: ActiveMotionAmplificationUpload | null,
): JobView {
  if (activeUpload?.jobId === job.jobId) {
    return {
      job,
      uploadProgress: activeUpload.progress,
      uploadState: activeUpload.progress < 100 ? "uploading" : "starting",
    };
  }
  return {
    job,
    uploadProgress:
      job.status === "Completed" ? 100 : (job.progressPercent ?? 0),
    uploadState: defaultUploadState(job),
  };
}

export function MotionAmplificationScreen({
  jobsQuery,
}: {
  jobsQuery: MotionAmplificationJobsQueryState;
}) {
  const { t } = useTranslation();
  const rawVideosQuery = useMotionAmplificationRawVideosQuery();
  const { uploadVideo, activeUpload } =
    useUploadMotionAmplificationVideoMutation();
  const retryJobMutation = useRetryMotionAmplificationJobMutation();
  const knownJobStatuses = useRef<
    Map<MotionAmplificationJobId, MotionAmplificationJob["status"]>
  >(new Map());
  const [failedJobsAwaitingDecision, setFailedJobsAwaitingDecision] = useState<
    FailedJobDecision[]
  >([]);
  const jobViews = (jobsQuery.data ?? [])
    .map((job) => toJobView(job, activeUpload))
    .sort((a, b) =>
      (b.job.dateCreated || "").localeCompare(a.job.dateCreated || ""),
    );
  const [filters, setFilters] = useState(defaultMotionAmplificationFilters);
  const visibleJobViews = jobViews.filter(({ job }) => job.status !== "Failed");
  const filteredJobViews = visibleJobViews.filter(({ job }) =>
    matchesMotionAmplificationFilters(job, filters),
  );
  const [selectedJobId, setSelectedJobId] =
    useState<MotionAmplificationJobId | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailsJobId, setDetailsJobId] =
    useState<MotionAmplificationJobId | null>(null);
  const detailsJob =
    jobViews.find(({ job }) => job.jobId === detailsJobId)?.job ?? null;
  const hasNoMatchingJobs =
    visibleJobViews.length > 0 && filteredJobViews.length === 0;

  useEffect(() => {
    const failedJobs: FailedJobDecision[] = [];

    for (const job of jobsQuery.data ?? []) {
      const previousStatus = knownJobStatuses.current.get(job.jobId);
      knownJobStatuses.current.set(job.jobId, job.status);
      if (
        previousStatus &&
        previousStatus !== "Failed" &&
        job.status === "Failed"
      ) {
        failedJobs.push({
          jobId: job.jobId,
          jobName: job.name ?? job.fileName ?? "",
        });
      }
    }

    if (failedJobs.length === 0) return;
    setFailedJobsAwaitingDecision((current) => [
      ...current,
      ...failedJobs.filter(
        ({ jobId }) => !current.some((job) => job.jobId === jobId),
      ),
    ]);
  }, [jobsQuery.data]);

  const failedJobAwaitingDecision = failedJobsAwaitingDecision[0] ?? null;

  function resolveFailedJobDecision(jobId: MotionAmplificationJobId) {
    setFailedJobsAwaitingDecision((current) =>
      current.filter((job) => job.jobId !== jobId),
    );
  }

  async function retryFailedJob(jobId: MotionAmplificationJobId) {
    await retryJobMutation.mutateAsync(jobId);
    resolveFailedJobDecision(jobId);
  }

  async function handleUpload(submission: Parameters<typeof uploadVideo>[0]) {
    const jobId = await uploadVideo(submission);
    setSelectedJobId(jobId);
    setUploadOpen(false);
  }

  return (
    <AppShellContent
      pageHeader={
        <Group justify="between" align="center">
          <Title size="lg">{t("motionAmplification.pageTitle")}</Title>
          <Button icon="add-plus" onClick={() => setUploadOpen(true)}>
            {t("motionAmplification.addVideo")}
          </Button>
        </Group>
      }
    >
      <Stack gap={24}>
        {jobsQuery.error && (
          <Text tone="negative">
            {jobsQuery.error.message || t("motionAmplification.loadJobsError")}
          </Text>
        )}
        <MotionAmplificationJobFilters
          filters={filters}
          jobs={jobViews.map(({ job }) => job)}
          onFiltersChange={setFilters}
        />
        {hasNoMatchingJobs ? (
          <Text tone="subtle">{t("motionAmplification.noSearchResults")}</Text>
        ) : (
          <MotionAmplificationGalleryAdapter
            jobs={filteredJobViews}
            isLoading={jobsQuery.isLoading}
            selectedJobId={selectedJobId}
            onSelectJob={setSelectedJobId}
            onShowDetails={(job) => setDetailsJobId(job.jobId)}
            onAddVideo={() => setUploadOpen(true)}
          />
        )}
        <MotionAmplificationUploadModal
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          onSubmit={handleUpload}
          rawVideos={rawVideosQuery.data ?? []}
          isLoadingRawVideos={rawVideosQuery.isLoading}
        />
        <MotionAmplificationDetailsModal
          job={detailsJob}
          open={detailsJobId !== null}
          onOpenChange={(open) => {
            if (!open) setDetailsJobId(null);
          }}
        />
        <MotionAmplificationRetryPrompt
          open={failedJobAwaitingDecision !== null}
          jobName={failedJobAwaitingDecision?.jobName ?? ""}
          isRetrying={retryJobMutation.isPending}
          onRetry={() => {
            if (failedJobAwaitingDecision) {
              void retryFailedJob(failedJobAwaitingDecision.jobId).catch(
                () => undefined,
              );
            }
          }}
          onDecline={() => {
            if (failedJobAwaitingDecision) {
              resolveFailedJobDecision(failedJobAwaitingDecision.jobId);
            }
          }}
        />
      </Stack>
    </AppShellContent>
  );
}
